import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { XIcon, ChevronRight, Maximize2, ArrowUpRight } from 'lucide-react';
import { useCopy } from './copy/copyMode';
type Persona = {
  id: string;
  role: string;
  name: string;
  headline: string;
  doing: string;
  tools: string[];
  examples: string[];
  outcome: string;
  taskletOutcome: string;
  color: string;
  image?: string;
  activeImage?: string;
};
const GROUP_IMAGE = 'https://cdn.magicpatterns.com/uploads/9XRofhQAk118LHvtkbKC4f/Group_60.png';
const PERSONAS: Persona[] = [{
  id: 'sales',
  role: 'Head of Sales',
  name: 'Sofia',
  image: 'https://cdn.magicpatterns.com/uploads/9jwxkRExcCBYEhh1GCsye3/Rectangle_(5).png',
  headline: 'Every lead followed up. Every deal moving.',
  doing: 'Reviving 4 stale deals with tailored outreach',
  tools: ['salesforce', 'gmail', 'slack'],
  examples: ['Write follow-ups for everyone I met this week', 'Tell me which deals are about to go cold', 'Update the CRM from my call notes'],
  outcome: 'So your Head of Sales spends their time closing, not updating the CRM.',
  taskletOutcome: 'Your Giga Head of Sales updates the CRM, so you can spend your time closing.',
  color: 'from-amber-500/20'
}, {
  id: 'cto',
  role: 'CTO',
  name: 'Aaron',
  image: 'https://cdn.magicpatterns.com/uploads/k9zuEY5MuYdrGKsbqc2qAT/Rectangle_(6).png',
  headline: 'Your roadmap moves while the busywork handles itself.',
  doing: 'Triaging 14 Sentry errors into Linear tickets',
  tools: ['github', 'linear', 'slack'],
  examples: ["Turn today's bug reports into tickets", 'Summarize what the team shipped this week', 'Write the postmortem from the incident thread'],
  outcome: 'So your CTO ships architecture, not issue tickets.',
  taskletOutcome: 'Your Giga CTO handles issue tickets, so you can ship architecture.',
  color: 'from-emerald-500/20'
}, {
  id: 'cmo',
  role: 'CMO',
  name: 'Devin',
  image: 'https://cdn.magicpatterns.com/uploads/g6BkGzwa9CwdyEJ6YT8GbH/Rectangle_(2).png',
  headline: 'Every dollar of spend earning its keep.',
  doing: 'Pausing ad sets under 0.8× ROAS in Meta',
  tools: ['google-analytics', 'twitter', 'tiktok'],
  examples: ['Show me which ads are wasting budget', "Recap how last week's campaigns did", 'Break down what each channel costs us'],
  outcome: 'So your CMO drives growth, not wrangling spreadsheets.',
  taskletOutcome: 'Your Giga CMO wrangles spreadsheets, so you can drive growth.',
  color: 'from-purple-500/20'
}, {
  id: 'content',
  role: 'Head of Content',
  name: 'Theo',
  headline: 'A content engine that never runs dry.',
  doing: 'Drafting a week of on-brand social posts',
  tools: ['twitter', 'tiktok', 'notion'],
  examples: ['Turn this launch into a week of posts', 'Rewrite this in our brand voice', 'Repurpose the webinar into a thread'],
  outcome: 'So your Head of Content crafts the story, not the busywork.',
  taskletOutcome: 'Your Giga Head of Content handles the busywork, so you can craft the story.',
  color: 'from-orange-500/40',
  image: 'https://cdn.magicpatterns.com/uploads/5BL2mbWeFKFZH1mcoCyf7Y/Rectangle_(4).png'
}, {
  id: 'designer',
  role: 'Head of Design',
  name: 'Anika',
  image: 'https://cdn.magicpatterns.com/uploads/unXpqjKJHSrjrYP1dypr2x/Rectangle_(3).png',
  headline: 'From brief to polished mockups in minutes.',
  doing: 'Turning the brief into 6 on-brand mockups in Figma',
  tools: ['figma', 'slack', 'notion'],
  examples: ['Turn the brief into a few mockups', 'Find inconsistencies in our design system', 'Round up the feedback from Slack'],
  outcome: 'So your Head of Design perfects the experience, not pushing pixels.',
  taskletOutcome: 'Your Giga Head of Design pushes pixels, so you can perfect the experience.',
  color: 'from-rose-500/30'
}, {
  id: 'cfo',
  role: 'CFO',
  name: 'Margot',
  image: 'https://cdn.magicpatterns.com/uploads/sVUmBUgvixiAk6C2Z9XavX/Mask_group_(7).png',
  headline: 'Books that practically close themselves.',
  doing: "Reconciling this month's P&L from Stripe → Xero",
  tools: ['stripe', 'microsoft-excel', 'quickbooks'],
  examples: ["Reconcile this month's numbers", 'Flag invoices that went over budget', 'Build a cash-flow forecast for next quarter'],
  outcome: 'So your CFO models the future, not reconciling the past.',
  taskletOutcome: 'Your Giga CFO reconciles the past, so you can model the future.',
  color: 'from-blue-500/20'
}];
function ToolChips({
  tools
}: {
  tools: string[];
}) {
  return <div className="flex items-center -space-x-1" data-id="element-62">
      {tools.map((tool, idx) => <div key={tool} className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-white border border-neutral-200 shadow-sm flex items-center justify-center relative" style={{
      zIndex: 10 - idx
    }} data-id="element-63">
          <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${tool}.svg`} alt={tool} className="w-2.5 h-2.5 md:w-3 md:h-3 object-contain" onError={e => {
        ;
        (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
      }} data-id="element-64" />
        </div>)}
    </div>;
}
// A single in-progress task row — pulsing status dot + task + animated progress
// shimmer, so the whole stack reads as concurrent work happening right now.
function LiveTaskRow({
  task,
  index
}: {
  task: string;
  index: number;
}) {
  const reduce = useReducedMotion();
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 16
  }} transition={{
    delay: 0.4 + index * 0.1,
    duration: 0.4
  }} className="relative overflow-hidden bg-white/70 rounded-2xl border border-[#EAE4D9] p-3.5 md:p-4 shadow-sm" data-id="element-65">
      <div className="flex items-center gap-3" data-id="element-66">
        <span className="relative flex h-2.5 w-2.5 shrink-0" data-id="element-67">
          <motion.span className="absolute inline-flex h-full w-full rounded-full bg-[#EA580C]" animate={reduce ? {} : {
          opacity: [0.75, 0, 0.75],
          scale: [1, 2.2, 1]
        }} transition={{
          duration: 1.8,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: index * 0.45
        }} data-id="element-68" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#EA580C]" data-id="element-69" />
        </span>
        <span className="text-[15px] md:text-base text-[#2B211A] font-medium leading-snug" data-id="element-70">
          {task}
        </span>
      </div>
      <div className="absolute left-0 right-0 bottom-0 h-[2px] bg-[#EA580C]/10 overflow-hidden" data-id="element-71">
        <motion.div className="h-full w-1/3 bg-[#EA580C]/70 rounded-full" animate={reduce ? {} : {
        x: ['-100%', '320%']
      }} transition={{
        duration: 2.4,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: index * 0.5
      }} data-id="element-72" />
      </div>
    </motion.div>;
}
// Desktop scatter positions — balanced constellation grid.
// Index order matches PERSONAS: 0 sales, 1 cto, 2 cmo, 3 content, 4 designer, 5 cfo.
const POSITIONS = [{
  top: '76%',
  left: '30%'
}, {
  top: '41%',
  left: '42%'
}, {
  top: '17%',
  left: '60%'
}, {
  top: '63%',
  left: '64%'
}, {
  top: '50%',
  left: '12%'
}, {
  top: '13%',
  left: '18%'
} // 5 cfo
];
// Harmonious size classes per persona
const SIZES = ['w-16 h-16 md:w-32 md:h-32', 'w-16 h-16 md:w-32 md:h-32', 'w-16 h-16 md:w-28 md:h-28', 'w-16 h-16 md:w-28 md:h-28', 'w-16 h-16 md:w-28 md:h-28', 'w-16 h-16 md:w-32 md:h-32' // 5 CFO
];
// Reveal order — instead of popping in top-to-bottom by source order, the
// constellation "lights up" from the center outward so it reads like the team
// assembling rather than a list loading. Indexed by PERSONAS order:
// 0 sales, 1 cto (center), 2 cmo, 3 content, 4 designer, 5 cfo.
const REVEAL_STEP = [1, 0, 2, 1, 3, 2];
export function AICoworkers({
  isTasklet = false
}: {
  isTasklet?: boolean;
}) {
  const copy = useCopy();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const reduce = useReducedMotion();
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpandedId(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const expandedPersona = PERSONAS.find(p => p.id === expandedId);
  return <section className="relative w-full bg-[#1A120C] overflow-hidden min-h-[640px] md:min-h-[820px] flex items-start justify-center" data-id="element-73">
      {/* Full-bleed iris background */}
      <motion.img aria-hidden="true" initial={{
      opacity: 0,
      scale: 1.05
    }} whileInView={{
      opacity: 1,
      scale: 1
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 1.2,
      ease: 'easeOut'
    }} src="https://cdn.magicpatterns.com/uploads/uKnUHUUB7yisBkazEitn6g/CleanShot_2026-06-18_at_13.29.212x.png" alt="" className="absolute inset-0 w-full h-full object-cover" data-id="element-74" />

      {/* Scrim for legibility — darken top/bottom, keep the iris glowing in the middle */}
      <div aria-hidden="true" className="absolute inset-0" style={{
      background: 'linear-gradient(to bottom, rgba(26,18,12,0.88) 0%, rgba(26,18,12,0.2) 32%, rgba(26,18,12,0.2) 64%, rgba(26,18,12,0.92) 100%)'
    }} data-id="element-75" />

      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10 w-full flex flex-col items-center text-center pt-14 md:pt-24 pb-16 md:pb-24" data-id="element-76">
        <span className="block text-xs font-semibold tracking-[5px] uppercase text-[#EA580C] mb-5 md:mb-6" data-id="element-77">
          One brain, infinite capability
        </span>
        <h2 className="text-3xl md:text-5xl font-title font-medium tracking-tight text-white leading-[1.1] mx-auto max-w-4xl" data-id="element-78">
          Connect everything, architect anything.
        </h2>
        <p className="mt-5 md:mt-6 text-base md:text-xl text-white/70 leading-relaxed max-w-2xl mx-auto" data-id="element-79">
          One AI brain with context across every tool, function, and person.
          That's why it can reconcile books, revive deals, ship code, and handle
          anything else you throw at it.
        </p>
      </div>
    </section>;
}